import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, checkPermission, UserRole } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  hasPermission: (requiredRole: UserRole) => boolean;
  loginWithAzure: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Start with no user - require explicit login
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;

  // Check if user is already authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      // Check for local admin demo user first
      const demoUser = localStorage.getItem('demo_user');
      if (token === 'local-admin-token' && demoUser) {
        try {
          const userData = JSON.parse(demoUser);
          setUser(userData);
          setLoading(false);
          return;
        } catch (error) {
        }
      }

      // Try API authentication
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('demo_user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const hasPermission = (requiredRole: UserRole): boolean => {
    if (!user) return false;
    return checkPermission(user.roles, requiredRole);
  };

  const loginWithAzure = async () => {
    try {
      const { auth_url } = await apiService.getAuthUrl();
      window.location.href = auth_url;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('demo_user');
    setUser(null);
    
    // Redirect to Azure AD logout endpoint
    // This will sign the user out of Azure AD and clear SSO session
    const tenantId = process.env.REACT_APP_AZURE_TENANT_ID || 'ebf7f753-9416-4b2d-acc6-ab8ba4f3e0ca';
    const authorityHost = process.env.REACT_APP_AZURE_AUTHORITY_HOST || 'login.microsoftonline.com';
    const postLogoutRedirectUri = encodeURIComponent(window.location.origin + '/auth/login');
    const azureLogoutUrl = `https://${authorityHost}/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;
    
    window.location.href = azureLogoutUrl;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    hasPermission,
    loginWithAzure,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
